import { Layout, LayoutAxis, PlotData, PlotMouseEvent } from "plotly.js";
import { useEffect, useMemo, useRef, useState } from "react";
import Plot from "react-plotly.js";
import { useTheme } from "../../theme";
import {
  INDEX_LABEL,
  eventAnchorId,
  eventTitle,
  fetchMap,
  formatIsoDate,
  formatSnapshotStamp,
  indexAverage,
  indexFlagsForEvent,
  snapshotValidTime,
} from "../../lib/explorer";
import type { IndexName } from "../../lib/explorer";
import europeCoast from "../../lib/europeCoast.json";
import {
  MAP_HOVER_TEMPLATE,
  buildMapCustomData,
  mapDailyStats,
} from "../../lib/mapFieldStats";
import type { MapPointValues } from "../../lib/mapFieldStats";
import type {
  BlockingEvent,
  BlockingMapSnapshot,
  CatalogDate,
  DataScope,
} from "../../types/explorer";
import { EventDatePicker } from "./EventDatePicker";
import { EventTiles } from "./EventTiles";
import {
  CNN_ONLY_TONE,
  EVENT_STAGE_TONE,
  PATTERN_TONE,
} from "./eventBadgeStyles";
import { ScopeBadge } from "./ScopeBadge";
import { Dropdown } from "../Dropdown";
import { GlossaryLink } from "../GlossaryLink";
import {
  patternGlossaryId,
  stageGlossaryId,
} from "../../content/siteCatalog";

type Layer = "z500" | "z500Raw" | "probability";
type ColorMapId = "rdbu" | "spectral" | "viridis" | "brand";

interface MapPreviewProps {
  dates: CatalogDate[];
  events: BlockingEvent[];
  selectedDate: string;
  onDateChange: (date: string) => void;
  scopeFilter: "all" | DataScope;
  onScopeFilterChange: (scope: "all" | DataScope) => void;
}

const cache = new Map<string, BlockingMapSnapshot>();

const COLORSCALES: Record<ColorMapId, string | Array<[number, string]>> = {
  rdbu: [
    [0, "#2166ac"],
    [0.25, "#67a9cf"],
    [0.5, "#f7f7f7"],
    [0.75, "#ef8a62"],
    [1, "#b2182b"],
  ],
  spectral: [
    [0, "#5e4fa2"],
    [0.25, "#66c2a5"],
    [0.5, "#ffffbf"],
    [0.75, "#f46d43"],
    [1, "#9e0142"],
  ],
  viridis: "Viridis",
  brand: [
    [0, "#05386B"],
    [0.35, "#379683"],
    [0.7, "#5CDB95"],
    [1, "#EDF5E1"],
  ],
};

const COLOR_LABEL: Record<ColorMapId, string> = {
  rdbu: "Blue–Red",
  spectral: "Spectral",
  viridis: "Viridis",
  brand: "Brand",
};

interface EventScale {
  eventId: string;
  z500: { min: number; max: number };
  z500Raw: { min: number; max: number } | null;
  probability: { min: number; max: number };
  peakDate: string;
  peakProbability: number;
}

interface PinnedPoint {
  lon: number;
  lat: number;
  values: MapPointValues;
}

const LAYER_TOGGLE: { id: Layer; label: string }[] = [
  { id: "z500Raw", label: "Z500" },
  { id: "z500", label: "Z500*" },
  { id: "probability", label: "Grad-CAM" },
];

const INDEX_AVERAGE_HELP =
  "Share of TM90, DG83, and S04 that flagged a block. Available only when all three labels are known.";

function layerField(
  snapshot: BlockingMapSnapshot | null,
  layer: Layer,
): number[][] {
  if (!snapshot) return [];
  if (layer === "z500Raw") return snapshot.z500Raw ?? [];
  if (layer === "z500") return snapshot.z500;
  return snapshot.blockingProbability;
}

function layerHasData(snapshot: BlockingMapSnapshot | null, layer: Layer): boolean {
  if (layer !== "z500Raw") return true;
  if (!snapshot) return true;
  return Boolean(snapshot.z500Raw?.length);
}

function layerTitle(layer: Layer): string {
  if (layer === "z500Raw") return "Z500";
  if (layer === "z500") return "Z500*";
  return "Grad-CAM";
}

function layerColorbar(layer: Layer): string {
  if (layer === "z500Raw") return "Z500";
  if (layer === "z500") return "Z500*";
  return "P";
}

function layerExtent(
  eventScale: EventScale | null,
  layer: Layer,
): { min: number; max: number } | undefined {
  if (!eventScale) return undefined;
  if (layer === "z500Raw") return eventScale.z500Raw ?? undefined;
  if (layer === "z500") return eventScale.z500;
  return eventScale.probability;
}

function fieldExtent(field: number[][]): { min: number; max: number } {
  let min = Infinity;
  let max = -Infinity;
  for (const row of field) {
    for (const value of row) {
      if (!Number.isFinite(value)) continue;
      if (value < min) min = value;
      if (value > max) max = value;
    }
  }
  if (!Number.isFinite(min) || !Number.isFinite(max)) return { min: 0, max: 1 };
  return { min, max };
}

function symmetricExtent(min: number, max: number): { min: number; max: number } {
  const span = Math.max(Math.abs(min), Math.abs(max), 1e-3);
  return { min: -span, max: span };
}

function formatMean(
  value: number | null | undefined,
  digits: number,
  unit = "",
  signed = false,
): string {
  if (typeof value !== "number") return "—";
  const zeroThreshold = 0.5 * 10 ** -digits;
  const displayValue = Math.abs(value) < zeroThreshold ? 0 : value;
  const sign = signed && displayValue > 0 ? "+" : "";
  return `${sign}${displayValue.toFixed(digits)}${unit}`;
}

function IndexCall({ value }: { value: boolean | null }) {
  if (value === null) {
    return <span className="text-faint">Not available</span>;
  }
  return (
    <span className={value ? "font-semibold text-accent" : "font-semibold text-danger"}>
      {value ? "Block" : "No block"}
    </span>
  );
}

function useCompactPlot() {
  const [compact, setCompact] = useState(false);
  useEffect(() => {
    if (typeof window.matchMedia !== "function") return undefined;
    const mq = window.matchMedia("(max-width: 639px)");
    const onChange = () => setCompact(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return compact;
}

function ProbabilityMeter({ value }: { value: number | undefined }) {
  const pct = typeof value === "number" ? Math.max(0, Math.min(100, value * 100)) : 0;
  return (
    <div
      className="mt-3 h-1.5 overflow-hidden rounded-full bg-elevated"
      role="meter"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(pct)}
      aria-label="CNN domain probability"
    >
      <div
        className="h-full rounded-full bg-accent transition-[width] duration-500"
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

export const MapPreview = ({
  dates,
  events,
  selectedDate,
  onDateChange,
  scopeFilter,
  onScopeFilterChange,
}: MapPreviewProps) => {
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const compactPlot = useCompactPlot();
  const [snapshot, setSnapshot] = useState<BlockingMapSnapshot | null>(null);
  const [layer, setLayer] = useState<Layer>("z500");
  const [colorMap, setColorMap] = useState<ColorMapId>("rdbu");
  const [error, setError] = useState<string | null>(null);
  const [playing, setPlaying] = useState(false);
  const [eventScale, setEventScale] = useState<EventScale | null>(null);
  const [pinnedPoint, setPinnedPoint] = useState<PinnedPoint | null>(null);
  const playTimer = useRef<number | null>(null);
  const mapCardRef = useRef<HTMLDivElement>(null);
  const [mapCardHeight, setMapCardHeight] = useState<number | null>(null);

  const visibleEvents = useMemo(() => {
    if (scopeFilter === "all") return events;
    return events.filter((event) => event.scope === scopeFilter);
  }, [events, scopeFilter]);

  const selectedEvent =
    visibleEvents.find(
      (event) => selectedDate >= event.startDate && selectedDate <= event.endDate,
    ) ??
    events.find(
      (event) => selectedDate >= event.startDate && selectedDate <= event.endDate,
    ) ??
    visibleEvents[0];

  const playDates = useMemo(() => {
    if (!selectedEvent) return [];
    return dates
      .filter(
        (d) =>
          d.date >= selectedEvent.startDate &&
          d.date <= selectedEvent.endDate &&
          (d.playGroup ?? selectedEvent.playGroup) ===
            (selectedEvent.playGroup ?? selectedEvent.id),
      )
      .map((d) => d.date);
  }, [dates, selectedEvent]);

  const stepIndex = playDates.indexOf(selectedDate);
  const canPrev = stepIndex > 0;
  const canNext = stepIndex >= 0 && stepIndex < playDates.length - 1;

  useEffect(() => {
    if (!selectedEvent) return;
    const inEvent =
      selectedDate >= selectedEvent.startDate && selectedDate <= selectedEvent.endDate;
    if (!inEvent) {
      setPlaying(false);
      onDateChange(selectedEvent.startDate);
    }
  }, [selectedEvent, selectedDate, onDateChange]);

  useEffect(() => {
    let cancelled = false;
    setError(null);

    const load = async (date: string) => {
      const hit = cache.get(date);
      if (hit) return hit;
      const data = await fetchMap(date);
      cache.set(date, data);
      return data;
    };

    load(selectedDate)
      .then((data) => {
        if (!cancelled) setSnapshot(data);
      })
      .catch((err: Error) => {
        if (!cancelled) {
          setSnapshot(null);
          setError(err.message);
        }
      });

    const idx = playDates.indexOf(selectedDate);
    for (const neighbor of [playDates[idx - 1], playDates[idx + 1]]) {
      if (neighbor) void load(neighbor).catch(() => undefined);
    }

    return () => {
      cancelled = true;
    };
  }, [selectedDate, playDates]);

  useEffect(() => {
    if (!selectedEvent || playDates.length === 0) {
      setEventScale(null);
      return undefined;
    }
    let cancelled = false;
    const eventId = selectedEvent.id;

    Promise.all(
      playDates.map(async (date) => {
        const hit = cache.get(date);
        if (hit) return hit;
        const data = await fetchMap(date);
        cache.set(date, data);
        return data;
      }),
    )
      .then((maps) => {
        if (cancelled) return;
        let zMin = Infinity;
        let zMax = -Infinity;
        let zRawMin = Infinity;
        let zRawMax = -Infinity;
        let pMin = Infinity;
        let pMax = -Infinity;
        let peakDate = maps[0]?.date ?? playDates[0];
        let peakProbability = -Infinity;
        let sawRaw = false;
        for (const map of maps) {
          const z = fieldExtent(map.z500);
          const p = fieldExtent(map.blockingProbability);
          zMin = Math.min(zMin, z.min);
          zMax = Math.max(zMax, z.max);
          pMin = Math.min(pMin, p.min);
          pMax = Math.max(pMax, p.max);
          if (map.z500Raw?.length) {
            const raw = fieldExtent(map.z500Raw);
            zRawMin = Math.min(zRawMin, raw.min);
            zRawMax = Math.max(zRawMax, raw.max);
            sawRaw = true;
          }
          const domain = map.meta.domainProbability ?? p.max;
          if (domain > peakProbability) {
            peakProbability = domain;
            peakDate = map.date;
          }
        }
        setEventScale({
          eventId,
          z500: symmetricExtent(zMin, zMax),
          z500Raw: sawRaw ? { min: zRawMin, max: zRawMax } : null,
          probability: {
            min: pMin === pMax ? 0 : pMin,
            max: pMax === pMin ? 1 : pMax,
          },
          peakDate,
          peakProbability,
        });
      })
      .catch(() => {
        if (!cancelled) setEventScale(null);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedEvent?.id, playDates.join("|")]);

  useEffect(() => {
    if (!playing || playDates.length < 2) return undefined;
    const step = () => {
      const i = playDates.indexOf(selectedDate);
      if (i < 0 || i >= playDates.length - 1) {
        setPlaying(false);
        return;
      }
      onDateChange(playDates[i + 1]);
    };
    playTimer.current = window.setInterval(step, 900);
    return () => {
      if (playTimer.current) window.clearInterval(playTimer.current);
    };
  }, [playing, playDates, selectedDate, onDateChange]);

  useEffect(() => {
    if (playDates.length < 2) setPlaying(false);
  }, [playDates.length]);

  const field = layerField(snapshot, layer);
  const scale = layerExtent(eventScale, layer);
  const customData = useMemo(() => buildMapCustomData(snapshot), [snapshot]);
  const dailyStats = useMemo(
    () => (snapshot ? mapDailyStats(snapshot) : null),
    [snapshot],
  );

  const lonSpan = snapshot
    ? snapshot.region.lonMax - snapshot.region.lonMin
    : 60;
  const latSpan = snapshot
    ? snapshot.region.latMax - snapshot.region.latMin
    : 45;

  const plotData = useMemo<Partial<PlotData>[]>(() => {
    const coastHalo = isDark ? "rgba(10,16,14,0.92)" : "rgba(8,12,20,0.88)";
    const coastCore = isDark ? "rgba(237,245,225,0.98)" : "rgba(255,255,255,0.96)";
    const traces: Partial<PlotData>[] = [
      {
        z: field,
        x: snapshot?.lons ?? [],
        y: snapshot?.lats ?? [],
        type: "contour",
        colorscale: COLORSCALES[colorMap],
        zmin: scale?.min,
        zmax: scale?.max,
        contours: {
          coloring: "heatmap",
          showlines: layer !== "probability",
        },
        colorbar: {
          title: { text: layerColorbar(layer) },
          thickness: compactPlot ? 10 : 12,
          len: compactPlot ? 0.72 : 0.82,
        },
        customdata: customData as unknown as PlotData["customdata"],
        hovertemplate: MAP_HOVER_TEMPLATE,
      },
      ...europeCoast.map((line) => ({
        x: line.x,
        y: line.y,
        type: "scatter" as const,
        mode: "lines" as const,
        line: { color: coastHalo, width: 3.1 },
        hoverinfo: "skip" as const,
        showlegend: false,
      })),
      ...europeCoast.map((line) => ({
        x: line.x,
        y: line.y,
        type: "scatter" as const,
        mode: "lines" as const,
        line: { color: coastCore, width: 1.15 },
        hoverinfo: "skip" as const,
        showlegend: false,
      })),
    ];
    return traces;
  }, [
    field,
    snapshot,
    layer,
    colorMap,
    isDark,
    eventScale,
    compactPlot,
    scale,
    customData,
  ]);

  const layout: Partial<Layout> = useMemo(
    () => ({
      autosize: true,
      paper_bgcolor: "transparent",
      plot_bgcolor: "transparent",
      font: { color: isDark ? "#EDF5E1" : "#05386B", family: "Outfit, sans-serif" },
      hoverlabel: {
        bgcolor: isDark ? "#101b17" : "#ffffff",
        bordercolor: "#5CDB95",
        font: {
          color: isDark ? "#EDF5E1" : "#05386B",
          family: "Outfit, sans-serif",
          size: compactPlot ? 12 : 13,
        },
        align: "left",
      },
      title: undefined,
      margin: compactPlot
        ? { t: 12, r: 40, b: 32, l: 36 }
        : { t: 20, r: 72, b: 58, l: 64 },
      dragmode: compactPlot ? false : "zoom",
      hovermode: "closest",
      xaxis: {
        title: compactPlot ? undefined : { text: "Longitude (°)" },
        ticksuffix: "°",
        range: snapshot
          ? [snapshot.region.lonMin, snapshot.region.lonMax]
          : [-15, 45],
        constrain: "domain",
        gridcolor: isDark ? "rgba(92,219,149,0.12)" : "rgba(55,150,131,0.2)",
        zerolinecolor: isDark ? "rgba(92,219,149,0.2)" : "rgba(55,150,131,0.3)",
      } as Partial<LayoutAxis>,
      yaxis: {
        title: compactPlot ? undefined : { text: "Latitude (°)" },
        ticksuffix: "°",
        range: snapshot
          ? [snapshot.region.latMin, snapshot.region.latMax]
          : [30, 75],
        scaleanchor: "x",
        scaleratio: 1,
        constrain: "domain",
        gridcolor: isDark ? "rgba(92,219,149,0.12)" : "rgba(55,150,131,0.2)",
        zerolinecolor: isDark ? "rgba(92,219,149,0.2)" : "rgba(55,150,131,0.3)",
      } as Partial<LayoutAxis>,
      transition: { duration: 380, easing: "cubic-in-out" },
    }),
    [isDark, snapshot, layer, compactPlot],
  );

  const domainProbability = snapshot?.meta.domainProbability;
  const scope = snapshot?.meta.scope ?? selectedEvent?.scope;
  const indexFlags = indexFlagsForEvent(selectedEvent);
  const indexMean = indexAverage(indexFlags);
  const canPlay = playDates.length > 1 && canNext;
  const isPeakDay = Boolean(
    eventScale && selectedDate === eventScale.peakDate,
  );
  const peakProbability =
    eventScale?.peakProbability ?? selectedEvent?.probability;

  const goTo = (date: string) => {
    setPlaying(false);
    onDateChange(date);
  };

  useEffect(() => {
    setPinnedPoint(null);
  }, [selectedDate]);

  const handlePlotClick = (event: PlotMouseEvent) => {
    if (!compactPlot) return;
    const point = event.points[0] as {
      x?: number | string;
      y?: number | string;
      customdata?: unknown;
    };
    if (!point || !Array.isArray(point.customdata)) return;
    setPinnedPoint({
      lon: Number(point.x),
      lat: Number(point.y),
      values: point.customdata as MapPointValues,
    });
  };

  useEffect(() => {
    if (layer === "z500Raw" && snapshot && !layerHasData(snapshot, "z500Raw")) {
      setLayer("z500");
    }
  }, [layer, snapshot]);

  const plotRevision = useMemo(() => {
    const seed = `${selectedDate}|${layer}|${colorMap}|${theme}|${eventScale?.z500.max ?? "a"}|${compactPlot ? "m" : "d"}`;
    let hash = 0;
    for (let i = 0; i < seed.length; i += 1) {
      hash = (hash * 31 + seed.charCodeAt(i)) | 0;
    }
    return hash;
  }, [selectedDate, layer, colorMap, theme, eventScale?.z500.max, compactPlot]);

  useEffect(() => {
    const el = mapCardRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const sync = () => {
      const sideBySide =
        typeof window.matchMedia === "function" &&
        window.matchMedia("(min-width: 1024px)").matches;
      setMapCardHeight(sideBySide && el.offsetHeight > 0 ? el.offsetHeight : null);
    };
    const observer = new ResizeObserver(sync);
    observer.observe(el);
    window.addEventListener("resize", sync);
    sync();
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", sync);
    };
  }, [snapshot, layer, compactPlot, isPeakDay, error]);

  const playControlClass =
    "btn-ghost min-h-11 flex-1 !px-2 !py-2 text-sm disabled:opacity-35 sm:min-h-0 sm:flex-none sm:!min-w-[5.5rem] sm:!px-3 sm:!py-2.5";

  return (
    <div className="mt-8">
      <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div className="min-w-0">
          <h2 className="font-display text-2xl font-semibold text-ink">
            Blocking map
          </h2>
          <p className="mt-1 text-sm text-muted">
            Toggle the atmosphere and the CNN&apos;s attention. Colour scale
            stays fixed for the event so days are comparable.
          </p>
          <p className="mt-1 text-xs text-faint">
            Temperature is contextual only and was not an input to the CNN.
          </p>
        </div>
        {scope ? <ScopeBadge scope={scope} /> : null}
      </div>

      {snapshot?.meta.outsideTrainingSeason ? (
        <div
          role="note"
          className="mb-5 rounded-xl border border-accent/40 bg-accent/10 px-4 py-3 text-sm leading-relaxed text-muted"
        >
          <strong className="text-accent">Out-of-season demonstration:</strong>{" "}
          this {snapshot.meta.season} map shows how the frozen CNN transfers to
          a season it never saw during training (
          {snapshot.meta.modelTrainingSeason ?? "JJA"} only). The response is
          promising evidence of generalisation; May labels are still needed
          before treating its probability as calibrated.
        </div>
      ) : null}

      <div className="mb-5 grid min-w-0 items-stretch gap-4 md:grid-cols-[minmax(0,1fr)_17rem]">
        <div className="panel flex h-72 min-h-0 flex-col px-3 py-3 sm:h-80">
          <p className="mb-2 text-xs font-semibold uppercase tracking-[0.14em] text-faint">
            Events
          </p>
          <EventTiles
            events={visibleEvents}
            selectedDate={selectedDate}
            scopeFilter={scopeFilter}
            onScopeFilterChange={(next) => {
              setPlaying(false);
              onScopeFilterChange(next);
            }}
            onSelectEvent={(event) => {
              const keep =
                selectedDate >= event.startDate && selectedDate <= event.endDate
                  ? selectedDate
                  : event.startDate;
              goTo(keep);
            }}
          />
        </div>
        <div className="panel min-h-0 overflow-y-auto px-3 py-3 md:h-80">
          <EventDatePicker
            availableDates={playDates}
            selectedDate={
              playDates.includes(selectedDate)
                ? selectedDate
                : playDates[0] ?? selectedDate
            }
            onSelectDate={goTo}
            peakDate={eventScale?.peakDate}
          />
        </div>
      </div>

      <div className="grid min-w-0 items-stretch gap-4 lg:grid-cols-[minmax(0,1fr)_21rem]">
        <div className="order-2 min-w-0 lg:order-1">
          <div ref={mapCardRef} className="panel flex min-w-0 flex-col overflow-hidden">
            <div className="flex flex-wrap items-center justify-between gap-2 px-3 pt-3">
              <div className="flex min-w-0 flex-1 rounded-lg border border-line/35 bg-bg/70 p-0.5">
                {LAYER_TOGGLE.filter((item) => layerHasData(snapshot, item.id)).map(
                  ({ id, label }) => (
                  <button
                    key={id}
                    type="button"
                    onClick={() => setLayer(id)}
                    className={[
                      "min-h-10 flex-1 rounded-md px-1.5 py-1.5 text-xs font-semibold transition sm:min-h-0 sm:px-3 sm:text-sm",
                      layer === id
                        ? "bg-accent/20 text-accent"
                        : "text-muted hover:text-ink",
                    ].join(" ")}
                  >
                    {label}
                  </button>
                  ),
                )}
              </div>
              <Dropdown
                label="Colour map"
                size="md"
                align="right"
                value={colorMap}
                onChange={setColorMap}
                options={(Object.keys(COLOR_LABEL) as ColorMapId[]).map((id) => ({
                  value: id,
                  label: COLOR_LABEL[id],
                }))}
              />
            </div>
            <div className="flex flex-col gap-1 px-3 pt-2 sm:flex-row sm:items-baseline sm:justify-between sm:gap-3">
              <p className="min-w-0 text-sm font-semibold text-ink">
                {snapshot
                  ? `${layerTitle(layer)} · ${formatSnapshotStamp(snapshot.date, snapshotValidTime(snapshot))}`
                  : "Blocking map"}
              </p>
              {isPeakDay ? (
                <p className="shrink-0 text-sm font-semibold text-accent sm:text-right">
                  Peak day
                </p>
              ) : eventScale?.peakDate ? (
                <p className="shrink-0 text-xs text-faint sm:text-right">
                  Colour scale fixed for this event · peak {formatIsoDate(eventScale.peakDate)}
                </p>
              ) : null}
            </div>
            {error ? (
              <p className="px-4 py-16 text-center text-sm text-danger">{error}</p>
            ) : (
              <div
                className="mx-auto w-full min-w-0 max-w-full flex-1 px-1"
                style={{ aspectRatio: `${lonSpan} / ${latSpan}` }}
              >
                <Plot
                  data={plotData}
                  layout={layout as Partial<Layout>}
                  config={{
                    responsive: true,
                    displayModeBar: false,
                    scrollZoom: false,
                  }}
                  style={{ width: "100%", height: "100%" }}
                  useResizeHandler
                  revision={plotRevision}
                  onClick={handlePlotClick}
                />
              </div>
            )}

            {compactPlot && pinnedPoint ? (
              <div
                className="mx-3 mb-2 rounded-xl border border-accent/40 bg-surface p-3 text-xs shadow-lg shadow-bg/20"
                role="status"
                aria-live="polite"
              >
                <div className="flex items-center justify-between gap-3">
                  <p className="font-semibold text-ink">
                    {pinnedPoint.lon.toFixed(0)}° lon ·{" "}
                    {pinnedPoint.lat.toFixed(0)}° lat
                  </p>
                  <button
                    type="button"
                    className="rounded-md px-2 py-1 font-semibold text-faint transition hover:bg-accent/10 hover:text-accent"
                    onClick={() => setPinnedPoint(null)}
                    aria-label="Close map reading"
                  >
                    Close ×
                  </button>
                </div>
                <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1.5 border-t border-line/25 pt-2 text-muted">
                  <span>Z500</span>
                  <span className="text-right text-ink">
                    {pinnedPoint.values[0]} gpm
                  </span>
                  <span>Z500*</span>
                  <span className="text-right text-ink">
                    {pinnedPoint.values[1]} gpm
                  </span>
                  <span>Grad-CAM</span>
                  <span className="text-right text-ink">
                    {pinnedPoint.values[2]}%
                  </span>
                  <span>Daily max T2m</span>
                  <span className="text-right text-ink">
                    {pinnedPoint.values[3]} °C
                  </span>
                </div>
              </div>
            ) : null}

            <div className="flex items-stretch gap-1.5 px-2 pb-3 pt-1 sm:flex-wrap sm:items-center sm:justify-center sm:gap-2 sm:px-3">
              <button
                type="button"
                disabled={!canPrev}
                onClick={() => canPrev && goTo(playDates[0])}
                className={playControlClass}
                aria-label="First day"
              >
                <span className="sm:hidden">«</span>
                <span className="hidden sm:inline">« Start</span>
              </button>
              <button
                type="button"
                disabled={!canPrev}
                onClick={() => canPrev && goTo(playDates[stepIndex - 1])}
                className={playControlClass}
                aria-label="Previous day"
              >
                <span className="sm:hidden">‹</span>
                <span className="hidden sm:inline">‹ Prev</span>
              </button>
              <button
                type="button"
                disabled={playing ? false : !canPlay}
                onClick={() => setPlaying((v) => !v)}
                className="btn-primary min-h-11 min-w-[4.5rem] flex-[1.2] !px-0 !py-2 text-sm disabled:opacity-35 sm:min-h-0 sm:w-24 sm:flex-none sm:!py-2.5"
              >
                {playing ? "Pause" : "Play"}
              </button>
              <button
                type="button"
                disabled={!canNext}
                onClick={() => canNext && goTo(playDates[stepIndex + 1])}
                className={playControlClass}
                aria-label="Next day"
              >
                <span className="sm:hidden">›</span>
                <span className="hidden sm:inline">Next ›</span>
              </button>
              <button
                type="button"
                disabled={!canNext}
                onClick={() => canNext && goTo(playDates[playDates.length - 1])}
                className={playControlClass}
                aria-label="Last day"
              >
                <span className="sm:hidden">»</span>
                <span className="hidden sm:inline">End »</span>
              </button>
              <p className="hidden whitespace-nowrap text-sm text-muted sm:ml-1 sm:block">
                {playDates.length > 0 && stepIndex >= 0
                  ? `${stepIndex + 1} / ${playDates.length}`
                  : "—"}
              </p>
            </div>
            <p className="px-3 pb-3 text-center text-xs text-muted sm:hidden">
              {playDates.length > 0 && stepIndex >= 0
                ? `Day ${stepIndex + 1} of ${playDates.length}`
                : "—"}
            </p>
          </div>
        </div>

        <aside
          className="panel order-1 flex min-h-0 flex-col overflow-y-auto p-4 lg:order-2"
          style={mapCardHeight ? { height: mapCardHeight } : undefined}
        >
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-accent">
              <GlossaryLink
                termId="p-blocked"
                className="uppercase tracking-[0.14em] text-info hover:underline"
              >
                CNN P(blocked)
              </GlossaryLink>
            </p>
            <div className="mt-3 flex items-end justify-between gap-3">
              <p className="font-display text-4xl font-semibold leading-none text-ink sm:text-5xl">
                {typeof domainProbability === "number"
                  ? `${(domainProbability * 100).toFixed(0)}%`
                  : "—"}
              </p>
              {typeof peakProbability === "number" ? (
                <p className="pb-0.5 text-right text-xs text-muted sm:text-sm">
                  Peak in window
                  <span className="mt-0.5 block font-semibold text-ink">
                    {(peakProbability * 100).toFixed(0)}%
                    {eventScale?.peakDate
                      ? ` · ${formatIsoDate(eventScale.peakDate)}`
                      : ""}
                  </span>
                </p>
              ) : null}
            </div>
            <ProbabilityMeter value={domainProbability} />
            <p className="mt-2 text-sm text-muted">
              Domain probability for{" "}
              {formatSnapshotStamp(selectedDate, snapshotValidTime(snapshot))}
            </p>
          </div>

          <div className="mt-4 border-t border-line/25 pt-4">
            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-faint">
              Daily European averages
            </p>
            <div className="mt-2 space-y-1.5">
              <div className="flex items-center justify-between gap-3 text-sm">
                <GlossaryLink
                  termId="z500"
                  className="text-muted hover:text-info hover:underline"
                >
                  Z500
                </GlossaryLink>
                <span className="font-semibold text-ink">
                  {formatMean(dailyStats?.z500Raw, 0, " gpm")}
                </span>
              </div>
              <div className="flex items-center justify-between gap-3 text-sm">
                <GlossaryLink
                  termId="z500-star"
                  className="text-muted hover:text-info hover:underline"
                >
                  Mean |Z500*|
                </GlossaryLink>
                <span className="font-semibold text-ink">
                  {formatMean(dailyStats?.z500MeanAbsoluteAnomaly, 1, " gpm")}
                </span>
              </div>
              <div className="flex items-center justify-between gap-3 text-sm">
                <GlossaryLink
                  termId="gradcam"
                  className="text-muted hover:text-info hover:underline"
                >
                  Grad-CAM
                </GlossaryLink>
                <span className="font-semibold text-ink">
                  {formatMean(
                    typeof dailyStats?.gradCam === "number"
                      ? dailyStats.gradCam * 100
                      : null,
                    0,
                    "%",
                  )}
                </span>
              </div>
              <div
                className="flex items-center justify-between gap-3 text-sm"
                title={
                  snapshot
                    ? `Source: ${
                        snapshot.meta.temperatureDataset ??
                        snapshot.meta.temperatureSource ??
                        snapshot.meta.source
                      }. ${snapshot.meta.temperatureSampling ?? ""}`
                    : undefined
                }
              >
                <GlossaryLink
                  termId="t2m-daily-max"
                  className="text-muted hover:text-info hover:underline"
                >
                  Daily max T2m
                </GlossaryLink>
                <span className="font-semibold text-ink">
                  {formatMean(dailyStats?.t2mMaxC, 1, " °C")}
                </span>
              </div>
            </div>
          </div>

          {selectedEvent ? (
            <div className="mt-4 border-t border-line/25 pt-3">
              <p className="text-sm font-semibold text-ink">
                {eventTitle(selectedEvent)}
              </p>
              <p className="mt-1 text-xs text-muted">{selectedEvent.region}</p>
              <p className="mt-1 text-xs text-faint">
                {formatIsoDate(selectedEvent.startDate)}
                {selectedEvent.endDate !== selectedEvent.startDate
                  ? ` – ${formatIsoDate(selectedEvent.endDate)}`
                  : ""}
                {playDates.length > 0 && stepIndex >= 0
                  ? ` · Day ${stepIndex + 1} of ${playDates.length}`
                  : ""}
              </p>
              <div
                aria-label="Event quick facts"
                className="mt-3 flex flex-wrap gap-1.5"
              >
                {selectedEvent.patternLabel ? (
                  (() => {
                    const patternTermId = patternGlossaryId(
                      selectedEvent.patternLabel,
                    );
                    const label = `Pattern · ${selectedEvent.patternLabel}`;
                    return patternTermId ? (
                      <GlossaryLink
                        termId={patternTermId}
                        title="Circulation pattern · definition on Science"
                        className={PATTERN_TONE}
                      >
                        {label}
                      </GlossaryLink>
                    ) : (
                      <span
                        title="Circulation pattern"
                        className={PATTERN_TONE}
                      >
                        {label}
                      </span>
                    );
                  })()
                ) : null}
                {selectedEvent.stage ? (
                  <GlossaryLink
                    termId={stageGlossaryId(selectedEvent.stage)}
                    title="Stage containing the event's peak CNN probability · definition on Science"
                    className={`rounded-full px-2 py-0.5 text-[11px] font-semibold capitalize ${EVENT_STAGE_TONE[selectedEvent.stage] ?? EVENT_STAGE_TONE.unknown}`}
                  >
                    {selectedEvent.stage}
                  </GlossaryLink>
                ) : null}
                {selectedEvent.cnnOnly ? (
                  <GlossaryLink
                    termId="cnn-only"
                    title="Flagged by the CNN but not by GTD, TM90, DG83 or S04"
                    className={CNN_ONLY_TONE}
                  >
                    CNN-only
                  </GlossaryLink>
                ) : null}
              </div>
              <a
                href={`#${eventAnchorId(selectedEvent)}`}
                className="mt-2 inline-flex text-sm font-semibold text-info hover:underline"
              >
                See more details about this event
              </a>
            </div>
          ) : null}

          <div className="mt-5 space-y-2 border-t border-line/25 pt-4 lg:mt-auto">
            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-faint">
              Other indices · this day
            </p>
            <div className="flex items-center justify-between gap-3 text-sm">
              <GlossaryLink
                termId="gtd"
                className="text-muted hover:text-info hover:underline"
              >
                GTD
              </GlossaryLink>
              <IndexCall value={indexFlags.GTD} />
            </div>
            {(Object.keys(INDEX_LABEL) as IndexName[]).map((name) => (
              <div
                key={name}
                className="flex items-center justify-between gap-3 text-sm"
              >
                <GlossaryLink
                  termId={name.toLowerCase()}
                  className="text-muted hover:text-info hover:underline"
                >
                  {INDEX_LABEL[name]}
                </GlossaryLink>
                <IndexCall value={indexFlags[name]} />
              </div>
            ))}
            <div className="flex items-center justify-between gap-3 border-t border-line/20 pt-2">
              <GlossaryLink
                termId="index-average"
                className="text-sm font-semibold text-ink hover:text-info hover:underline"
              >
                Index average
              </GlossaryLink>
              <span className="text-sm font-semibold text-ink">
                {indexMean === null ? (
                  <span
                    className="cursor-help border-b border-dotted border-faint"
                    title={INDEX_AVERAGE_HELP}
                    aria-label={`Not available. ${INDEX_AVERAGE_HELP}`}
                  >
                    —
                  </span>
                ) : (
                  `${Math.round(indexMean * 100)}%`
                )}
              </span>
            </div>
            <p className="text-[11px] leading-snug text-faint">
              Share of{" "}
              <GlossaryLink
                termId="tm90"
                className="text-faint hover:text-info hover:underline"
              >
                TM90
              </GlossaryLink>{" "}
              /{" "}
              <GlossaryLink
                termId="dg83"
                className="text-faint hover:text-info hover:underline"
              >
                DG83
              </GlossaryLink>{" "}
              /{" "}
              <GlossaryLink
                termId="s04"
                className="text-faint hover:text-info hover:underline"
              >
                S04
              </GlossaryLink>{" "}
              that flagged a block. Shown only when all three labels are known.
            </p>
          </div>
        </aside>
      </div>
    </div>
  );
};
